import { Ban, Plus, Search } from "lucide-react"
import { motion, useReducedMotion } from "motion/react"
import { useState } from "react"

import { Muestra } from "@/componentes/sistema/Muestra"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { MenuAcciones } from "@/componentes/ui/MenuAcciones"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { cn } from "@/lib/cn"

/**
 * La guía del sistema de diseño, viva.
 *
 * ⚠ **Viva quiere decir que usa los componentes de verdad, no capturas.** Una guía
 * pintada aparte se desincroniza el primer día que alguien cambia un relleno, y a
 * partir de ahí miente con toda la autoridad de estar escrita. Aquí cada muestra
 * importa el mismo `Boton`, la misma `Tabla` y las mismas clases que las pantallas,
 * así que no puede decir algo distinto de lo que el sistema hace.
 *
 * ⚠ **Los colores no están escritos: se leen del tema.** Cada muestra pinta
 * `var(--token)`, así que al alternar claro y oscuro la guía cambia con el sistema
 * en vez de documentar una de las dos paletas.
 */

const COLORES: { token: string; nombre: string; uso: string }[] = [
  { token: "--primary", nombre: "Primario", uso: "RELLENO: botones, barras, chips activos" },
  { token: "--primary-fg", nombre: "Sobre primario", uso: "Texto encima del relleno primario" },
  {
    token: "--primary-texto",
    nombre: "Primario como tinta",
    // ⚠ No es el mismo color que el relleno, y por eso está catalogado aparte: el
    // borgoña es oscuro, y en modo oscuro el relleno que admite una etiqueta
    // blanca encima reprobaría usado como texto.
    uso: "TINTA: enlaces, iconos, anillos de foco, el ítem activo del menú",
  },
  {
    token: "--primary-suave",
    nombre: "Primario lavado",
    uso: "Fondos teñidos: la fila seleccionada, el día de hoy",
  },
  { token: "--background", nombre: "Fondo", uso: "El lienzo de la aplicación" },
  { token: "--surface", nombre: "Superficie", uso: "Tarjetas, tablas, formularios" },
  { token: "--surface-elevated", nombre: "Superficie elevada", uso: "Cabeceras y realces" },
  { token: "--border", nombre: "Borde", uso: "Separadores y contornos" },
  { token: "--fg", nombre: "Texto", uso: "El contenido" },
  { token: "--fg-muted", nombre: "Texto atenuado", uso: "Rótulos y datos secundarios" },
  { token: "--success", nombre: "Éxito", uso: "Cobrado, pagado, activo" },
  { token: "--warning", nombre: "Aviso", uso: "Vencimientos y pendientes" },
  { token: "--destructive", nombre: "Peligro", uso: "Errores y lo irreversible" },
  { token: "--info", nombre: "Información", uso: "Estados en proceso" },
]

const TONOS: Tono[] = ["neutro", "primario", "exito", "aviso", "peligro", "info"]

const MOVIMIENTO = [
  { token: "--duracion-rapida", ms: 120, uso: "Cambios de color al apuntar" },
  { token: "--duracion-entrada", ms: 180, uso: "Aparecer: paneles, menús, filas nuevas" },
  { token: "--duracion-salida", ms: 120, uso: "Desaparecer, siempre más rápido que entrar" },
]

/** Los tres estados del cristal, en el orden en que se escalan. */
const ESTADOS_CRISTAL = [
  { nombre: "En reposo", clase: "" },
  { nombre: "Al pasar por encima", clase: "toque-cristal" },
  { nombre: "Elegido", clase: "activo-cristal" },
] as const

export function SistemaDeDiseno() {
  const sinMovimiento = useReducedMotion()
  const [pestana, setPestana] = useState("uno")
  const [animando, setAnimando] = useState(0)

  return (
    <div className="space-y-8 pb-16">
      <header>
        <h1 className="text-xl font-semibold">Sistema de diseño</h1>
        <p className="mt-1 max-w-[68ch] text-sm" style={{ color: "var(--fg-muted)" }}>
          Todo lo que se puede usar para construir una pantalla, con su código al lado. Las muestras
          usan los componentes de verdad: si algo cambia, esta página cambia con ello.
        </p>
      </header>

      <Muestra
        indice={0}
        titulo="Color"
        descripcion="Doce tokens, y ninguno se escribe en hexadecimal dentro de un componente. Cambian solos con el tema."
        codigo={`style={{ color: "var(--fg-muted)" }}`}
      >
        <div className="grid w-full gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {COLORES.map((c) => (
            <div key={c.token} className="flex items-center gap-3">
              <span
                aria-hidden={true}
                className="h-9 w-9 shrink-0 rounded-[var(--radius-control)] border"
                style={{ backgroundColor: `var(${c.token})`, borderColor: "var(--border)" }}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{c.nombre}</span>
                <span
                  className="block truncate text-xs"
                  style={{ color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}
                >
                  {c.token}
                </span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {c.uso}
                </span>
              </span>
            </div>
          ))}
        </div>
      </Muestra>

      <Muestra
        indice={1}
        titulo="Botones"
        descripcion="Cuatro variantes y dos tamaños. «Peligro» se reserva para lo que no se deshace con otro clic; usarla para «Cancelar» la gasta."
        codigo={`<Boton variante="primario">Guardar</Boton>
<Boton variante="secundario">Cancelar</Boton>
<Boton variante="fantasma" tamano="sm">Editar</Boton>
<Boton variante="peligro">Cerrar expediente</Boton>`}
      >
        <Boton variante="primario">Guardar</Boton>
        <Boton variante="secundario">Cancelar</Boton>
        <Boton variante="fantasma" tamano="sm">
          Editar
        </Boton>
        <Boton variante="peligro">Cerrar expediente</Boton>
        <Boton variante="primario" disabled>
          Deshabilitado
        </Boton>
      </Muestra>

      <Muestra
        indice={2}
        titulo="Insignias"
        descripcion="El texto siempre se escribe: el color no puede ser el único portador de significado, y en blanco y negro no lo es para nadie."
        codigo={`<Insignia tono="exito">Cobrado</Insignia>`}
      >
        {TONOS.map((t) => (
          <Insignia key={t} tono={t}>
            {t}
          </Insignia>
        ))}
      </Muestra>

      <Muestra
        indice={3}
        titulo="Campos"
        descripcion="Una sola clase para todo control de formulario. El foco se ve siempre: quien navega con teclado necesita saber dónde está."
        codigo={`<input className="campo" />
<select className="campo">…</select>`}
      >
        <label className="text-sm">
          Texto
          <input className="campo mt-1" placeholder="Importadora Caribe, C.A." />
        </label>
        <label className="text-sm">
          Selector
          <select className="campo mt-1">
            <option>Una opción</option>
          </select>
        </label>
        <label className="text-sm">
          Deshabilitado
          <input className="campo mt-1" value="No se toca" disabled readOnly />
        </label>
      </Muestra>

      <Muestra
        indice={4}
        titulo="Tarjetas"
        descripcion="La superficie de todo lo que se agrupa: un formulario, un panel, un bloque de la guía."
        codigo={`<div className="tarjeta">…</div>`}
      >
        <div className="tarjeta w-full">
          <p className="text-sm font-medium">Almacenaje en puerto</p>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Contenido de la tarjeta.
          </p>
        </div>
      </Muestra>

      <Muestra
        indice={5}
        titulo="Tablas"
        descripcion="Toda tabla del sistema pasa por estos primitivos. Las celdas con cifras llevan `numero`: sin eso los dígitos bailan al paginar."
        codigo={`<Tabla etiqueta="Qué contiene">
  <Cabecera><Th>Concepto</Th><Th derecha>Monto</Th></Cabecera>
  <Cuerpo>
    <Fila><Td>Almacenaje</Td><Td numero derecha>1.250,00</Td></Fila>
  </Cuerpo>
</Tabla>`}
      >
        <div className="w-full">
          <Tabla etiqueta="Ejemplo de tabla">
            <Cabecera>
              <Th>Concepto</Th>
              <Th>Estado</Th>
              <Th derecha>Monto</Th>
            </Cabecera>
            <Cuerpo>
              <Fila>
                <Td>Almacenaje en puerto</Td>
                <Td>
                  <Insignia tono="aviso">Pendiente</Insignia>
                </Td>
                <Td numero derecha>
                  1.250,00 USD
                </Td>
              </Fila>
              <Fila atenuada>
                <Td>Transporte interno</Td>
                <Td>
                  <Insignia tono="peligro">Anulada</Insignia>
                </Td>
                <Td numero derecha>
                  420,00 USD
                </Td>
              </Fila>
            </Cuerpo>
          </Tabla>
        </div>
      </Muestra>

      <Muestra
        indice={6}
        titulo="Tabla vacía"
        descripcion="Un vacío se dice, no se deja en blanco: «no hay nada» y «no se pudo cargar» son respuestas distintas."
        codigo={`<TablaVacia colSpan={3}>Todavía no hay nada aquí</TablaVacia>`}
      >
        <div className="w-full">
          <Tabla etiqueta="Ejemplo de tabla vacía">
            <Cabecera>
              <Th>Concepto</Th>
              <Th derecha>Monto</Th>
            </Cabecera>
            <Cuerpo>
              <TablaVacia colSpan={2}>Todavía no hay nada aquí</TablaVacia>
            </Cuerpo>
          </Tabla>
        </div>
      </Muestra>

      <Muestra
        indice={7}
        titulo="Pestañas"
        descripcion="Con el patrón de WAI-ARIA entero: una sola parada de tabulador para el grupo, y las flechas cambian de pestaña. Del mismo cristal que el menú: la elegida sube la luz, nada se rellena."
        codigo={`<Pestanas
  pestanas={[{ valor: "uno", etiqueta: "Uno" }]}
  activa={activa}
  onCambiar={setActiva}
  etiqueta="Qué grupo es"
  idPanel="mi-panel"
  prefijoId="mi-pestana"
/>`}
      >
        <div className="w-full">
          <Pestanas
            pestanas={[
              { valor: "uno", etiqueta: "Primera" },
              { valor: "dos", etiqueta: "Segunda" },
              { valor: "tres", etiqueta: "Tercera" },
            ]}
            activa={pestana}
            onCambiar={setPestana}
            etiqueta="Pestañas de ejemplo"
            idPanel="panel-ejemplo"
            prefijoId="ejemplo"
          />
          <div id="panel-ejemplo" role="tabpanel" aria-labelledby={`ejemplo-${pestana}`}>
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Contenido de la pestaña {pestana}.
            </p>
          </div>
        </div>
      </Muestra>

      <Muestra
        indice={8}
        titulo="Movimiento"
        descripcion="Tres duraciones y una curva. Salir siempre es más rápido que entrar: lo que se va no merece la espera de lo que llega."
        codigo={`import { motion, useReducedMotion } from "motion/react"

const sinMovimiento = useReducedMotion()
<motion.div
  initial={sinMovimiento ? false : { opacity: 0, y: 8 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
/>`}
      >
        <div className="w-full space-y-3">
          <ul className="space-y-1 text-sm">
            {MOVIMIENTO.map((m) => (
              <li key={m.token} className="flex flex-wrap items-baseline gap-2">
                <code
                  className="text-xs"
                  style={{ color: "var(--primary-texto)", fontFamily: "var(--font-mono)" }}
                >
                  {m.token}
                </code>
                <span className="tabular-nums text-xs" style={{ color: "var(--fg-muted)" }}>
                  {m.ms} ms
                </span>
                <span style={{ color: "var(--fg-muted)" }}>· {m.uso}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <Boton variante="secundario" tamano="sm" onClick={() => setAnimando((n) => n + 1)}>
              Ver la entrada
            </Boton>
            <motion.span
              key={animando}
              initial={sinMovimiento ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            >
              <Insignia tono="primario">Así entra un elemento</Insignia>
            </motion.span>
          </div>

          {sinMovimiento && (
            <p role="status" className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Tu sistema pide movimiento reducido, así que aquí no se anima nada. Es la preferencia
              mandando, no un fallo.
            </p>
          )}
        </div>
      </Muestra>

      <Muestra
        indice={9}
        titulo="El cristal"
        descripcion="El armazón —menú, encabezado y área de trabajo— no se apoya en el fondo del tema: flota sobre una fotografía. Eso lo convierte en un SEGUNDO material, con sus propios estados, y confundirlo con la superficie del tema es lo que produce parches."
        codigo={`<nav className="marco-cristal">
  <NavLink className={({ isActive }) => cn(
    "foco-cristal",
    isActive ? "activo-cristal" : "toque-cristal",
  )} />
</nav>`}
      >
        <div className="w-full space-y-3">
          {/* La muestra se pinta SOBRE el cristal de verdad, no sobre la superficie
              del tema: un estado de cristal enseñado sobre fondo opaco miente, que
              es justo lo que esta página existe para no hacer. */}
          <div className="marco-cristal space-y-1 rounded-[var(--radius-tarjeta)] p-3">
            {ESTADOS_CRISTAL.map((e) => (
              <div
                key={e.clase}
                className={cn(
                  "foco-cristal flex items-center justify-between gap-3 rounded-[var(--radius-control)] px-3 py-2 text-sm",
                  e.clase,
                )}
                style={{ color: "rgb(var(--marco-tinta) / 0.85)" }}
              >
                <span>{e.nombre}</span>
                <code className="text-xs" style={{ fontFamily: "var(--font-mono)" }}>
                  .{e.clase || "—"}
                </code>
              </div>
            ))}
            <button
              type="button"
              className="foco-cristal toque-cristal w-full rounded-[var(--radius-control)] px-3 py-2 text-left text-sm"
              style={{ color: "rgb(var(--marco-tinta) / 0.85)" }}
            >
              Tabula hasta aquí para ver el foco
            </button>
          </div>

          <ul className="space-y-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            <li>
              <strong>La luz sube, nada se rellena.</strong> Un estado aclara el cristal un punto;
              no pinta un rectángulo encima. <code>--surface-elevated</code> y{" "}
              <code>--primary-suave</code> están calibradas contra la superficie del TEMA y aquí son
              un parche claro.
            </li>
            <li>
              <strong>
                El foco es un <code>outline</code>, no un <code>ring</code>.
              </strong>{" "}
              El anillo de Tailwind se dibuja con <code>box-shadow</code>, y ahí ya vive el filo de{" "}
              <code>.activo-cristal</code>: uno pisaría al otro y el ítem activo perdería su
              contorno justo al tabular hasta él.
            </li>
            <li>
              <strong>Un solo color de foco en todo el sistema.</strong> El acceso lo tuvo en el
              vino de marca y el marco en blanco; con dos, quien navega con teclado aprende dos
              señales para la misma cosa.
            </li>
          </ul>
        </div>
      </Muestra>

      <Muestra
        indice={10}
        titulo="Menú de acciones"
        descripcion="Un botón que agrupa lo que se puede hacer con lo que tiene debajo. No sustituye a la acción primaria: ésa va fuera, como botón propio. Cada opción dice su consecuencia debajo, y lo que no se deshace va en rojo y aparte. Las flechas lo recorren y Escape lo cierra devolviendo el foco al botón."
        codigo={`<MenuAcciones
  opciones={[
    { id: "buscar", etiqueta: "Buscar en Movimientos", ayuda: "Con fechas", onSelect: buscar },
    { id: "anular", etiqueta: "Anular", peligrosa: true, separador: true, onSelect: anular },
  ]}
/>`}
      >
        {/* Con alto de sobra: el panel se despliega hacia abajo y la sección siguiente
            se pintaría encima de lo que se saliera de esta tarjeta. */}
        <div className="flex min-h-72 w-full items-start justify-end">
          <MenuAcciones
            opciones={[
              {
                id: "buscar",
                etiqueta: "Buscar en Movimientos",
                ayuda: "Con fechas, también lo anterior al extracto",
                icono: <Search size={16} />,
                onSelect: () => undefined,
              },
              {
                id: "registrar",
                etiqueta: "Registrar movimiento",
                ayuda: "Intereses, comisiones, correcciones",
                icono: <Plus size={16} />,
                onSelect: () => undefined,
                separador: true,
              },
              {
                id: "anular",
                etiqueta: "Anular",
                ayuda: "No se deshace con otro clic",
                icono: <Ban size={16} />,
                onSelect: () => undefined,
                peligrosa: true,
                separador: true,
              },
            ]}
          />
        </div>
      </Muestra>
    </div>
  )
}
